import React from "react";

const references = [
  "Bianchi, R., Schonfeld, I. S., and Laurent, E. (2015). Burnout-depression overlap: A review. Clinical Psychology Review, 36, 28-41. https://doi.org/10.1016/j.cpr.2015.01.004",
  "Brown, A., and Maydeu-Olivares, A. (2011). Item response modeling of forced-choice questionnaires. Educational and Psychological Measurement, 71(3), 460-502. https://doi.org/10.1177/0013164410375112",
  "Brown, A., and Maydeu-Olivares, A. (2013). How IRT can solve problems of ipsative data in forced-choice questionnaires. Psychological Methods, 18(1), 36-52. https://doi.org/10.1037/a0030641",
  "Demerouti, E., Bakker, A. B., Vardakou, I., and Kantas, A. (2003). The convergent validity of two burnout instruments: A multitrait-multimethod analysis. European Journal of Psychological Assessment, 19(1), 12-23. https://doi.org/10.1027/1015-5759.19.1.12",
  "Freudenberger, H. J. (1974). Staff burn-out. Journal of Social Issues, 30(1), 159-165. https://doi.org/10.1111/j.1540-4560.1974.tb00706.x",
  "Hontangas, P. M., de la Torre, J., Ponsoda, V., Leenen, I., Morillo, D., and Abad, F. J. (2015). Comparing traditional and IRT scoring of forced-choice tests. Applied Psychological Measurement, 39(8), 598-612. https://doi.org/10.1177/0146621615585851",
  "Leiter, M. P., and Maslach, C. (1999). Six areas of worklife: A model of the organizational context of burnout. Journal of Health and Human Services Administration, 21(4), 472-489.",
  "Maslach, C., and Jackson, S. E. (1981). The measurement of experienced burnout. Journal of Occupational Behaviour, 2(2), 99-113. https://doi.org/10.1002/job.4030020205",
  "Maslach, C., and Leiter, M. P. (1997). The truth about burnout: How organizations cause personal stress and what to do about it. Jossey-Bass.",
  "Poghosyan, L., Aiken, L. H., and Sloane, D. M. (2009). Factor structure of the Maslach Burnout Inventory: An analysis of data from large scale cross-sectional surveys of nurses from eight countries. International Journal of Nursing Studies, 46(7), 894-902. https://doi.org/10.1016/j.ijnurstu.2009.03.004",
  "Reis, D., Xanthopoulou, D., and Tsaousis, I. (2015). Measuring job and academic burnout with the Oldenburg Burnout Inventory (OLBI): Factorial invariance across samples and countries. Burnout Research, 2(1), 8-18. https://doi.org/10.1016/j.burn.2014.11.001",
  "Roskam, I., Brianda, M.-E., and Mikolajczak, M. (2018). A step forward in the conceptualization and measurement of parental burnout: The Parental Burnout Assessment (PBA). Frontiers in Psychology, 9, 758. https://doi.org/10.3389/fpsyg.2018.00758",
  "Schaufeli, W. B., Desart, S., and De Witte, H. (2020). Burnout Assessment Tool (BAT): Development, validity, and reliability. International Journal of Environmental Research and Public Health, 17(24), 9495. https://doi.org/10.3390/ijerph17249495",
  'World Health Organization. (2019, May 28). Burn-out an "occupational phenomenon": International Classification of Diseases. https://www.who.int/news/item/28-05-2019-burn-out-an-occupational-phenomenon-international-classification-of-diseases',
];

const MethodologyClosing: React.FC = () => (
  <div className="space-y-6 text-sm md:text-base leading-relaxed text-[#380C1E]/90 font-light">
    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        10. Limitations
      </h2>
      <p className="mb-3">
        <strong>Ipsative scores.</strong> This is the instrument's most
        important limitation and it is inherent to its design. A domain score of
        70 percent means the respondent chose that domain on 7 of the 10 screens
        where it appeared. It does not mean she is "70 percent burned out" in
        that domain, and it cannot be compared to another respondent's 70
        percent. The scores describe the shape of one person's profile and
        nothing else. Forced-choice instruments can be scored to recover
        normative information using item response models (Brown and
        Maydeu-Olivares, 2011, 2013), but that requires calibration data the
        instrument does not yet have.
      </p>
      <p className="mb-3">
        <strong>No psychometric evidence.</strong> As of this version there is
        no published evidence of reliability (internal consistency, test-retest)
        or validity (structural, convergent, predictive) for this instrument.
        The domain structure comes from the author's coaching practice and has
        not been confirmed by factor analysis or any other statistical method.
        Claims that the quiz "works" are, at present, claims about design intent
        and practitioner experience, not measurement.
      </p>
      <p className="mb-3">
        <strong>Coarse resolution.</strong> With 10 binary opportunities per
        domain, scores move in 10-point steps and the primary and runner-up are
        frequently within the two-point blend window. The instrument is built to
        accept this and report blends honestly rather than to force a single
        label, but a longer instrument would resolve more finely.
      </p>
      <p className="mb-3">
        <strong>Two-item severity.</strong> Duration and impact are single items
        each. The severity score is a rough sort, not a measure.
      </p>
      <p className="mb-3">
        <strong>Single-author item pool.</strong> All items were written and
        mapped by the framework author with editorial support. There has been no
        independent expert review of item-to-domain mapping and no cognitive
        interviewing with respondents.
      </p>
      <p className="mb-3">
        <strong>Self-selected respondents.</strong> People who take a free
        burnout quiz on a coaching site are, by definition, people who already
        suspect they are burned out and who found this site. Any future response
        data will describe that population, not women in general.
      </p>
      <p className="mb-3">
        <strong>Response effects not yet tested.</strong> Order effects, fatigue
        across 33 screens, and the effect of the halfway transition screen have
        not been measured.
      </p>
      <p className="mb-3">
        <strong>Convergence indicator is weak by design.</strong> The two-stem
        check reports only whether the primary earned at least one point under
        each stem. It is a floor, not a statistic.
      </p>
      <p className="mb-3">
        <strong>Retake behavior.</strong> Because a retake overwrites prior
        scores, the instrument cannot currently support within-person change
        analysis without a separate data design.
      </p>
      <p>
        <strong>Previous versions.</strong> Versions 2 and 3 used rating scales
        and different item sets. Results from those versions are not comparable
        to version 4 and are not included in any future analysis.
      </p>
    </section>

    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        11. Validation roadmap
      </h2>
      <p className="mb-3">
        The author intends to publish the following when the data exist, in this
        order, and to update this document's version number when she does.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Descriptive distribution:</strong> the distribution of primary
          domains, blend frequency, severity bands, and care-flag rate across
          all version 4 completions, with the completion count stated.
        </li>
        <li>
          <strong>Design checks:</strong> the rate at which the primary domain
          converges across both stems; the frequency of ties; the frequency with
          which each of the 30 pairs is answered in each direction, which is the
          direct test of the weight-matching assumption in Section 7.
        </li>
        <li>
          <strong>Reliability:</strong> test-retest agreement for primary domain
          and severity band over a short interval, using a separate retake path
          that preserves prior scores.
        </li>
        <li>
          <strong>Convergent validity:</strong> correlation between the severity
          score and an established, freely available burnout measure such as the
          OLBI or the BAT, administered to a volunteer subsample. The MBI is a
          licensed instrument and would require a separate arrangement.
        </li>
        <li>
          <strong>Structural checks:</strong> whether the six-domain structure
          is supported by the pattern of choices, using methods appropriate to
          forced-choice data.
        </li>
        <li>
          <strong>Expert review:</strong> independent review of the
          item-to-domain mapping by at least one practitioner outside the
          author's organization.
        </li>
      </ul>
      <p className="mt-3">
        Until the first of these is published, the correct description of this
        instrument is "a structured reflection tool with a documented design,"
        and that is the only description the author will make.
      </p>
    </section>

    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        12. How to cite this document
      </h2>
      <p>
        McArthur, I. (2026). Where Is Your Burnout Coming From? Design, Scoring,
        and Limitations of a Six-Domain Burnout Source Inventory (Version 1.0).
        iamieshiadanielle.com. Retrieved from
        https://iamieshiadanielle.com/burnout-quiz-methodology
      </p>
      <p className="mt-3">
        Readers are welcome to link to, quote from, and critique this document.
        Corrections and challenges to the design are invited through the contact
        page on the site and will be acknowledged in subsequent versions.
      </p>
    </section>

    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        References
      </h2>
      <ul className="list-disc pl-6 space-y-2 text-sm">
        {references.map((ref) => (
          <li key={ref}>{ref}</li>
        ))}
      </ul>
    </section>

    <p className="text-xs text-[#380C1E]/70 pt-6 border-t border-[#E8DEC8]">
      Copyright 2026 Ieshia Danielle. This methodology document may be linked
      to, quoted, and cited with attribution. The quiz, its questions, scoring,
      and result types are the intellectual property of Ieshia Danielle and may
      not be reproduced or adapted.{" "}
      <a
        href="https://iamieshiadanielle.com/product-license"
        className="text-[#9E1B50] underline"
      >
        iamieshiadanielle.com/product-license
      </a>
    </p>
  </div>
);

export default MethodologyClosing;
